import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams, Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import { isValidSlugFormat, extractSchoolSlugFromPath } from '../utils/routing';

const API_URL = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

interface School {
  slug: string;
  name: string;
  description?: string;
  email?: string;
  phone?: string;
  websiteUrl?: string;
  // IANA timezone (e.g. "Australia/Sydney") from the `School.timezone` COLUMN — the single
  // source of truth since #40 (2026-09-20), served by `GET /api/public/schools/:slug`. Used
  // to render calendar/schedule times in the school's zone rather than the viewer's browser
  // tz. ⚠️ It used to come from `settings.schoolInfo.timezone`, which diverged from what the
  // backend sweeps actually use; do not point it back there.
  timezone?: string;
  branding: {
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    accentMint?: string;
    accentCoral?: string;
    accentCream?: string;
  };
  settings?: any;
}

interface TenantContextType {
  school: School | null;
  schoolSlug: string | null;
  loading: boolean;
  error: string | null;
  refetchSchool: () => Promise<void>;
}

/**
 * The tab title when NO school slug is in the URL — the platform root routes,
 * which mount no TenantProvider. Must match the <title> in index.html.
 */
export const PLATFORM_TITLE = "Music 'n Me";

/**
 * A readable stand-in for the school name while the tenant payload is in flight:
 * "rhythm-kids" → "Rhythm Kids". Only ever shown for the moment before the real
 * name arrives, and never persisted — the API name always wins.
 */
export const titleFromSlug = (slug: string): string =>
  slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ') || PLATFORM_TITLE;

export const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const params = useParams<{ schoolSlug: string }>();

  // Extract school slug from pathname as fallback
  const schoolSlugFromPath = extractSchoolSlugFromPath(location.pathname);
  const schoolSlug = params.schoolSlug || schoolSlugFromPath;

  const [school, setSchool] = useState<School | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSchool = async () => {
    if (!schoolSlug) {
      setLoading(false);
      return;
    }

    // Validate slug format before making API call
    if (!isValidSlugFormat(schoolSlug)) {
      setError('Invalid school identifier');
      setSchool(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/api/v1/public/schools/${schoolSlug}`);
      setSchool(response.data);
    } catch (err: any) {
      console.error('Error fetching school:', err);

      // Provide specific error messages based on status code
      const status = err.response?.status;
      if (status === 404) {
        setError('School not found. Please check the URL and try again.');
      } else if (status === 403) {
        setError('This school is currently inactive.');
      } else if (status === 400) {
        setError('Invalid school identifier.');
      } else {
        setError(err.response?.data?.error || 'Failed to load school. Please try again later.');
      }

      setSchool(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchool();
  }, [schoolSlug]);

  // The browser tab names the SCHOOL whenever a slug is in the URL, never the
  // platform (Andrew, 2026-09-10). Every tenant page — login, register, all four
  // role dashboards — renders under this one provider, so this covers all of them.
  //
  // The URL carries the slug but only the API carries the NAME, so on a cold load
  // there is a window where a school is in scope and unnamed. The slug fills it
  // (titleFromSlug: "rhythm-kids" → "Rhythm Kids") so the tab never flashes the
  // platform name on a school page. The platform name is only for root routes,
  // which mount no provider at all.
  useEffect(() => {
    if (!schoolSlug) return;

    document.title = school?.name?.trim() || titleFromSlug(schoolSlug);
    return () => {
      document.title = PLATFORM_TITLE;
    };
  }, [schoolSlug, school?.name]);

  return (
    <TenantContext.Provider
      value={{
        school,
        schoolSlug: schoolSlug || null,
        loading,
        error,
        refetchSchool: fetchSchool,
      }}
    >
      {children || <Outlet />}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

/**
 * The current school's IANA timezone, falling back to the app/DB default 'Australia/Sydney'
 * when the tenant hasn't loaded yet or the field is absent (older payloads). Use this to format
 * calendar/schedule times so every viewer sees the school's local time, not their own.
 */
export const DEFAULT_SCHOOL_TIMEZONE = 'Australia/Sydney';
export const useSchoolTimezone = (): string => {
  const { school } = useTenant();
  return school?.timezone ?? DEFAULT_SCHOOL_TIMEZONE;
};
